import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import { Button } from "@/components/ui/button";
import { Phone, ArrowRight } from "lucide-react";
import { Link } from "@/lib/router-compat";
import SEOHead from "@/components/SEOHead";
import { SITE_URL } from "@/hooks/use-seo";
const topicCards = [
  {
    title: "When Anxiety, Insomnia and Depression Overlap",
    description:
      "How racing thoughts, poor sleep, and low mood can reinforce one another.",
    href: "/depression-anxiety-insomnia",
  },
  {
    title: "My Brain Won't Shut Off at Night",
    description:
      "Why your mind races when you're exhausted, and when it may be time to seek help.",
    href: "/brain-wont-shut-off",
  },
  {
    title: "I Never Had Depression Before. Why Now?",
    description:
      "Depression can appear without a previous history. Learn why evaluation matters.",
    href: "/sudden-depression",
  },
  {
    title: "When a Panic Attack Changes Everything",
    description:
      "What can happen after a frightening panic episode, and how clinicians evaluate it.",
    href: "/panic-anxiety-depression",
  },
  {
    title: "Can Past Trauma Affect You Years Later?",
    description:
      "How trauma can resurface, and how hypervigilance, sleep, and mood can overlap.",
    href: "/when-past-trauma-resurfaces",
  },
  {
    title: "“They're Just Not Themselves Anymore.”",
    description:
      "Changes families often notice, and how to help someone you love seek care.",
    href: "/helping-someone-with-depression",
  },
  {
    title: "When Antidepressants Aren't Enough",
    description:
      "What clinicians consider when medication hasn't provided enough relief.",
    href: "/when-antidepressants-are-not-enough",
  },
  {
    title: "Am I a Candidate for TMS?",
    description:
      "What clinicians evaluate when determining TMS eligibility and candidacy.",
    href: "/am-i-a-candidate",
  },
  {
    title: "What Happens During a TMS Consultation?",
    description:
      "What to expect during an evaluation, from symptoms to insurance and next steps.",
    href: "/what-to-expect-from-your-consultation",
  },
];
const webinarTopics = [
  "My Brain Won't Shut Off: Anxiety, Sleep & Depression",
  "I Was Fine Until I Wasn't: Understanding Sudden Depression",
  "They're Not Themselves: Helping Someone You Love",
  "When a Panic Attack Changes Everything",
  "When Past Trauma Resurfaces",
  "What If Depression Medication Isn't Working?",
  "Am I a Candidate for TMS?",
  "Standard vs Accelerated TMS",
  "Why Depression Treatment Isn't One-Size-Fits-All",
  "What Happens During a Neuro Wellness Evaluation?",
];
export default function MentalHealthEducation() {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={"Mental Health Education | Neuro Wellness"}
        description={
          "Explore educational resources about depression, anxiety, insomnia, panic, trauma, TMS and helping someone you love."
        }
        canonical={"/mental-health-resources"}
        keywords={
          "mental health education, depression resources, anxiety and sleep, TMS education, helping someone with depression"
        }
        schemaJson={[
          {
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            name: "Mental Health Education",
            description:
              "Explore educational resources about depression, anxiety, insomnia, panic, trauma, TMS and helping someone you love.",
            url: `${SITE_URL}/mental-health-resources`,
            provider: {
              "@id": "https://neurowellnesstms.com/#organization",
            },
          },
        ]}
      />

      <Navbar />
      <main className="pt-16 sm:pt-20">
        {/* HERO */}
        <section className="relative text-primary-foreground py-16 sm:py-28 overflow-hidden bg-primary">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/95 via-primary/90 to-primary-deep/90" />
          <div className="container-px relative mx-auto max-w-5xl px-4">
            <div className="max-w-2xl text-left space-y-5">
              <span className="inline-block rounded-full bg-accent/90 backdrop-blur-sm px-5 py-2 text-xs sm:text-sm font-bold text-white shadow-lg uppercase tracking-wider">
                Mental Health Education
              </span>
              <h1 className="font-headings text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight drop-shadow-md">
                Understand What You're Experiencing
              </h1>
              <p className="text-base sm:text-lg text-primary-foreground/90 leading-relaxed drop-shadow-md">
                Depression rarely exists as a single checkbox. Explore practical
                education about mood, anxiety, sleep, trauma, treatment options
                and how to help someone you love.
              </p>
              <div className="flex flex-col sm:flex-row items-start justify-start gap-4 pt-3">
                <Button
                  asChild
                  size="lg"
                  className="w-full sm:w-auto bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90 text-base px-8 py-6"
                >
                  <a href="/check-my-insurance-options">
                    Check My Insurance Options
                  </a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto border-2 border-white/60 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 hover:border-white/80 text-base px-8 py-6"
                >
                  <a href="tel:9548279793">
                    <Phone className="h-5 w-5 mr-2" />
                    Call 954-827-9793
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* TOPIC CARDS */}
        <section className="bg-background py-12 sm:py-16">
          <div className="container-px mx-auto max-w-5xl px-4">
            <h2 className="font-headings text-2xl sm:text-3xl font-extrabold text-primary mb-2">
              Explore Educational Topics
            </h2>
            <p className="text-foreground/70 text-base sm:text-lg mb-8 max-w-2xl">
              Each resource explains a common experience in plain language, what
              clinicians look for, and what the next step may look like.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {topicCards.map((card, i) => (
                <Link
                  key={i}
                  to={card.href}
                  className="group rounded-2xl bg-card border border-border p-6 shadow-soft hover:border-accent/40 hover:shadow-premium transition-all"
                >
                  <h3 className="font-headings text-lg font-bold text-primary mb-2 group-hover:text-accent transition-colors">
                    {card.title}
                  </h3>
                  <p className="text-foreground/75 text-sm leading-relaxed">
                    {card.description}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-accent">
                    Read more
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* WEBINAR / INFO SESSIONS */}
        <section className="bg-light-blue/30 py-12 sm:py-16">
          <div className="container-px mx-auto max-w-5xl px-4">
            <h2 className="font-headings text-2xl sm:text-3xl font-extrabold text-primary mb-2">
              Upcoming Mental Health Info Sessions
            </h2>
            <p className="text-foreground/70 text-base sm:text-lg mb-8 max-w-2xl">
              We're developing a series of educational info sessions on the
              topics below. Get notified when sessions are scheduled.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {webinarTopics.map((topic, i) => (
                <div
                  key={i}
                  className="rounded-2xl bg-card border border-border p-5 shadow-soft flex items-center justify-between gap-3"
                >
                  <span className="font-headings text-base font-bold text-primary">
                    {topic}
                  </span>
                  <span className="shrink-0 rounded-full bg-accent/15 px-3 py-1 text-xs font-semibold text-accent">
                    Coming Soon
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <Button
                asChild
                size="lg"
                className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
              >
                <a href="/contactus">Get Notified About Upcoming Sessions</a>
              </Button>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="bg-gradient-navy py-14 sm:py-20">
          <div className="container-px mx-auto max-w-2xl px-4 text-center space-y-5">
            <h2 className="font-headings text-2xl sm:text-3xl font-bold text-white">
              You Don't Have to Figure This Out Alone
            </h2>
            <p className="text-primary-foreground/80 text-base sm:text-lg leading-relaxed">
              Understanding what you're experiencing starts with a conversation.
              Our team can help you make sense of the pattern and what options
              may be appropriate.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Button
                asChild
                size="lg"
                className="w-full sm:w-auto bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
              >
                <a href="/check-my-insurance-options">
                  Check My Insurance Options
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-2 border-white/50 bg-transparent text-white hover:bg-white/15 hover:border-white/70"
              >
                <a href="/contactus">Schedule a Private Consultation</a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
